import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import {
    uploadImage,
    getUserMedia,
    deleteMedia,
    updateMediaAlt,
    formatFileSize,
    isImageFile,
    isSupportedImageFormat
} from '../services/mediaService';
import styles from '../styles/pages/MediaPage.module.scss';

const MediaPage = () => {
    const { currentUser } = useAuth();
    const [mediaList, setMediaList] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [copySuccessId, setCopySuccessId] = useState(null);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [dragActive, setDragActive] = useState(false);
    const [editingAlt, setEditingAlt] = useState(null);
    const [altText, setAltText] = useState('');
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (currentUser) {
            loadMedia();
        }
    }, [currentUser]);

    const loadMedia = async () => {
        try {
            setIsLoading(true);
            const media = await getUserMedia(currentUser.uid);
            setMediaList(media);
        } catch (error) {
            setError('メディアの読み込みに失敗しました');
            console.error('メディア読み込みエラー:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        validateAndSetFiles(files);
    };

    const validateAndSetFiles = (files) => {
        const validFiles = [];
        const errors = [];

        files.forEach(file => {
            if (!isImageFile(file)) {
                errors.push(`${file.name}: 画像ファイルではありません`);
                return;
            }

            if (!isSupportedImageFormat(file)) {
                errors.push(`${file.name}: サポートされていない形式です`);
                return;
            }

            if (file.size > 10 * 1024 * 1024) { // 10MB制限
                errors.push(`${file.name}: ファイルサイズが大きすぎます（最大10MB）`);
                return;
            }

            validFiles.push(file);
        });

        if (errors.length > 0) {
            setError(errors.join('\n'));
            return;
        }

        setSelectedFiles(validFiles);
        setError('');
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            const files = Array.from(e.dataTransfer.files);
            validateAndSetFiles(files);
        }
    };

    const handleUpload = async () => {
        if (selectedFiles.length === 0) return;

        try {
            setUploading(true);
            setUploadProgress(0);
            setError('');

            for (let i = 0; i < selectedFiles.length; i++) {
                const file = selectedFiles[i];
                await uploadImage(file, currentUser.uid);
                setUploadProgress(((i + 1) / selectedFiles.length) * 100);
            }

            setSuccess(`${selectedFiles.length}個のファイルをアップロードしました`);
            setSelectedFiles([]);
            setUploadProgress(0);
            loadMedia(); // リストを更新
        } catch (error) {
            setError('アップロードに失敗しました');
            console.error('アップロードエラー:', error);
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (mediaId, storagePath, fileName) => {
        if (!window.confirm(`「${fileName}」を削除しますか？`)) return;

        try {
            await deleteMedia(mediaId, storagePath);
            setSuccess('ファイルを削除しました');
            loadMedia(); // リストを更新
        } catch (error) {
            setError('削除に失敗しました');
            console.error('削除エラー:', error);
        }
    };

    const copyToClipboard = (url, mediaId) => {
        navigator.clipboard.writeText(url).then(() => {
            setCopySuccessId(mediaId);
            setTimeout(() => {
                setCopySuccessId(null);
            }, 2000);
        }).catch(() => {
            setError('コピーに失敗しました');
        });
    };

    const formatDate = (date) => {
        return new Date(date.toDate()).toLocaleString('ja-JP');
    };

    // alt属性の編集を開始
    const startEditAlt = (mediaId, currentAlt) => {
        setEditingAlt(mediaId);
        setAltText(currentAlt || '');
    };

    // alt属性の編集をキャンセル
    const cancelEditAlt = () => {
        setEditingAlt(null);
        setAltText('');
    };

    // alt属性を保存
    const saveAlt = async (mediaId) => {
        try {
            await updateMediaAlt(mediaId, altText);
            setMediaList(prev =>
                prev.map(media =>
                    media.id === mediaId
                        ? { ...media, altText: altText }
                        : media
                )
            );
            setEditingAlt(null);
            setAltText('');
            setSuccess('alt属性を更新しました');
            setTimeout(() => setSuccess(''), 3000);
        } catch (error) {
            setError('alt属性の更新に失敗しました');
            console.error('alt属性更新エラー:', error);
        }
    };

    if (isLoading) {
        return (
            <div>
                <Header />
                <div className={styles.loading}>読み込み中...</div>
            </div>
        );
    }

    return (
        <div>
            <Header />
            <div className={styles.container}>
                {/* エラー・成功メッセージ */}
                {error && (
                    <div className={styles.error}>
                        {error.split('\n').map((line, index) => (
                            <div key={index}>{line}</div>
                        ))}
                    </div>
                )}
                {success && (
                    <div className={styles.success}>{success}</div>
                )}

                {/* アップロードエリア */}
                <div className={styles.uploadSection}>
                    <h2>画像をアップロード</h2>

                    <div
                        className={`${styles.dropZone} ${dragActive ? styles.dragActive : ''}`}
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            accept="image/*"
                            onChange={handleFileSelect}
                            style={{ display: 'none' }}
                        />
                        <div className={styles.dropZoneContent}>
                            <div className={styles.uploadIcon}>📁</div>
                            <p>クリックまたはドラッグ&ドロップで画像を選択</p>
                            <p className={styles.supportedFormats}>
                                対応形式: JPG, PNG, GIF, WebP, SVG (最大10MB)
                            </p>
                        </div>
                    </div>

                    {/* 選択されたファイル */}
                    {selectedFiles.length > 0 && (
                        <div className={styles.selectedFiles}>
                            <h3>選択されたファイル ({selectedFiles.length})</h3>
                            <div className={styles.fileList}>
                                {selectedFiles.map((file, index) => (
                                    <div key={index} className={styles.fileItem}>
                                        <span>{file.name}</span>
                                        <span className={styles.fileSize}>
                                            ({formatFileSize(file.size)})
                                        </span>
                                    </div>
                                ))}
                            </div>
                            <button
                                onClick={handleUpload}
                                disabled={uploading}
                                className={styles.uploadButton}
                            >
                                {uploading ? 'アップロード中...' : 'アップロード'}
                            </button>

                            {uploading && (
                                <div className={styles.progressBar}>
                                    <div
                                        className={styles.progressFill}
                                        style={{ width: `${uploadProgress}%` }}
                                    ></div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* メディア一覧 */}
                <div className={styles.mediaSection}>
                    <h2>アップロード済み画像 ({mediaList.length})</h2>

                    {mediaList.length === 0 ? (
                        <div className={styles.emptyState}>
                            <p>まだ画像がアップロードされていません</p>
                        </div>
                    ) : (
                        <div className={styles.mediaGrid}>
                            {mediaList.map((media) => (
                                <div key={media.id} className={styles.mediaItem}>
                                    <div className={styles.mediaImage}>
                                        <img
                                            src={media.downloadURL}
                                            alt={media.altText || media.originalName}
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                                e.target.nextSibling.style.display = 'flex';
                                            }}
                                        />
                                        <div className={styles.imageError} style={{ display: 'none' }}>
                                            画像の読み込みに失敗しました
                                        </div>
                                    </div>

                                    <div className={styles.mediaInfo}>
                                        <div className={styles.fileName}>
                                            {media.originalName}
                                        </div>
                                        <div className={styles.fileDetails}>
                                            <span>{formatFileSize(media.fileSize)}</span>
                                            <span>{formatDate(media.uploadedAt)}</span>
                                        </div>

                                        {/* alt属性の表示・編集 */}
                                        <div className={styles.altSection}>
                                            <label className={styles.altLabel}>alt属性:</label>
                                            {editingAlt === media.id ? (
                                                <div className={styles.altEdit}>
                                                    <input
                                                        type="text"
                                                        value={altText}
                                                        onChange={(e) => setAltText(e.target.value)}
                                                        placeholder="alt属性を入力してください"
                                                        className={styles.altInput}
                                                        autoFocus
                                                    />
                                                    <div className={styles.altEditActions}>
                                                        <button
                                                            onClick={() => saveAlt(media.id)}
                                                            className={styles.saveAltButton}
                                                        >
                                                            保存
                                                        </button>
                                                        <button
                                                            onClick={cancelEditAlt}
                                                            className={styles.cancelAltButton}
                                                        >
                                                            キャンセル
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className={styles.altDisplay}>
                                                    <span className={styles.altText}>
                                                        {media.altText || '未設定'}
                                                    </span>
                                                    <button
                                                        onClick={() => startEditAlt(media.id, media.altText)}
                                                        className={styles.editAltButton}
                                                    >
                                                        編集
                                                    </button>
                                                </div>
                                            )}
                                        </div>

                                        <div className={styles.mediaActions}>
                                            {copySuccessId === media.id && (
                                                <div className={styles.copySuccess}>
                                                    コピーしました。
                                                </div>
                                            )}
                                            <button
                                                onClick={() => copyToClipboard(media.downloadURL, media.id)}
                                                className={styles.copyButton}
                                            >
                                                URLをコピー
                                            </button>
                                            <button
                                                onClick={() => handleDelete(media.id, media.storagePath, media.originalName)}
                                                className={styles.deleteButton}
                                            >
                                                削除
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MediaPage; 